import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  changeUserStatus,
  confirmUserPermanent,
  createUser,
  deleteUser,
  getUsers,
  getUserById,
  recoverUser,
  triggerUserPasswordReset,
  adminSetUserPassword,
  terminateUser,
  updateCompensation,
  updateEmployment,
  updateUser,
  updateUserRole,
} from '@/api/services/users.service';
import { forceLogoutUser } from '@/api/services/auth.service';
import { getErrorMessage } from '@/lib/getErrorMessage';
import { adminPasswordResetOutcome } from '../utils/adminPasswordReset';
import { usePermittedQuery } from '@/hooks/usePermittedQuery';
import type { AdminSetPasswordRequest } from '@/types/api.types';
import type {
  AssignRolePayload,
  ChangeStatusPayload,
  ConfirmPermanentPayload,
  CreateUserPayload,
  TerminateUserPayload,
  UpdateCompensationPayload,
  UpdateEmploymentPayload,
  UpdateUserPayload,
  UserListQueryParams,
} from '@/types';

export const usersQueryKeys = {
  all: ['users'] as const,
  list: (params?: UserListQueryParams) => ['users', 'list', params] as const,
  detail: (id: string) => ['users', 'detail', id] as const,
};

export function useUsersList(params?: UserListQueryParams, options?: { enabled?: boolean }) {
  return usePermittedQuery({
    module: 'users',
    action: 'read',
    queryKey: usersQueryKeys.list(params),
    queryFn: () => getUsers(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled,
  });
}

export function useUser(id: string | null) {
  return usePermittedQuery({
    module: 'users',
    action: 'read',
    queryKey: usersQueryKeys.detail(id ?? ''),
    queryFn: () => getUserById(id!),
    enabled: !!id,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateUserPayload) => createUser(payload),
    onSuccess: (user) => {
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.all });
      toast.success(
        `User created successfully. A password setup email has been sent to ${user.email}.`
      );
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments-overview'] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments-overview-me'] });
      queryClient.invalidateQueries({ queryKey: ['shifts-roster'] });
      queryClient.invalidateQueries({ queryKey: ['attendance', 'me', 'today'] });
      toast.success('User deleted');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useRecoverUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => recoverUser(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.detail(id) });
      // Recovery reactivates company memberships — refresh member lists and shift views.
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments-overview'] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments-overview-me'] });
      queryClient.invalidateQueries({ queryKey: ['shifts-roster'] });
      toast.success('User recovered');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateUserPayload }) =>
      updateUser(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.detail(variables.id) });
      // Member rows embed the user's name/email — refresh company member lists.
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast.success('User updated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateCompensation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateCompensationPayload }) =>
      updateCompensation(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.detail(variables.id) });
      // Members workspace resolves effectiveCompensation from user compensation.
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      // No success toast here: The profile panel saves profile + compensation together
      // and the profile update already surfaces a single "User updated" confirmation.
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateEmployment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateEmploymentPayload }) =>
      updateEmployment(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.detail(variables.id) });
      // The profile panel saves profile + employment together; keep one success toast.
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useChangeUserStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ChangeStatusPayload }) =>
      changeUserStatus(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.detail(variables.id) });
      // Status changes surface in member rows and affect who is assignable to shifts.
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments-overview'] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments-overview-me'] });
      queryClient.invalidateQueries({ queryKey: ['shifts-roster'] });
      toast.success('Status updated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useConfirmUserPermanent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: ConfirmPermanentPayload }) =>
      confirmUserPermanent(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.detail(variables.id) });
      toast.success('Employee confirmed as permanent');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useTerminateUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: TerminateUserPayload }) =>
      terminateUser(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments-overview'] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments-overview-me'] });
      queryClient.invalidateQueries({ queryKey: ['shifts-roster'] });
      // Terminating ends every open People & HR employment.
      queryClient.invalidateQueries({ queryKey: ['hr-employments'] });
      queryClient.invalidateQueries({ queryKey: ['hr-employee-profile'] });
      toast.success('Employee terminated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AssignRolePayload }) =>
      updateUserRole(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.all });
      queryClient.invalidateQueries({ queryKey: usersQueryKeys.detail(variables.id) });
      toast.success('Role updated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useForceLogoutUser() {
  return useMutation({
    mutationFn: (id: string) => forceLogoutUser(id),
    onSuccess: () => {
      toast.success('Signed out of all devices');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useTriggerUserPasswordReset() {
  return useMutation({
    mutationFn: ({ id }: { id: string }) => triggerUserPasswordReset(id),
    onSuccess: (result) => {
      const outcome = adminPasswordResetOutcome(result);
      if (outcome.level === 'success') toast.success(outcome.message);
      else toast.warning(outcome.message);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Admin sets the password directly. The caller shows the result, so no toast
 * on success; the dialog keeps the password on screen to copy.
 */
export function useAdminSetUserPassword() {
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & AdminSetPasswordRequest) =>
      adminSetUserPassword(id, body),
  });
}
